import { useMemo, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFetch } from "@/lib/hooks";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";

const sel = "h-9 w-full rounded-md border bg-background px-2 text-sm";

export function NewDraftDialog({ open, onOpenChange, onCreated }) {
  const { api } = useWorkspace();
  const contacts = useFetch(open ? "/contacts" : null);
  const products = useFetch(open ? "/product-profiles" : null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ contact_id: "", product_profile_id: "", purpose: "Introduce our product and ask for a short call", tone: "friendly" });
  const [busy, setBusy] = useState(false);
  const list = useMemo(() => (contacts.data || []).filter((c) => c.consent_status !== "opted_out" &&
    `${c.first_name || ""} ${c.last_name} ${c.job_title || ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 200), [contacts.data, q]);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/email-drafts/generate", { ...f, product_profile_id: f.product_profile_id || null }); toast.success("Draft ready for review"); onCreated(data); onOpenChange(false); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="email-new-dialog">
        <DialogHeader><DialogTitle>Draft an email with AI</DialogTitle>
          <DialogDescription>Uses only verified facts about the contact's company and your product profile. Nothing is sent — the draft goes to the approval queue.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Contact</Label>
            <Input placeholder="Search contacts" value={q} onChange={(e) => setQ(e.target.value)} data-testid="email-new-contact-search" />
            <select className={sel} size={5} value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} data-testid="email-new-contact">
              {list.map((c) => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(" ")}{c.job_title ? ` — ${c.job_title}` : ""}{c.email ? "" : " (no email yet)"}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label>Product to mention (optional)</Label>
            <select className={sel} value={f.product_profile_id} onChange={(e) => setF({ ...f, product_profile_id: e.target.value })} data-testid="email-new-product">
              <option value="">None</option>{(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label>What should the email do?</Label><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="email-new-purpose" /></div>
          <div className="space-y-1.5"><Label>Tone</Label>
            <select className={sel} value={f.tone} onChange={(e) => setF({ ...f, tone: e.target.value })} data-testid="email-new-tone"><option value="friendly">Friendly</option><option value="formal">Formal</option></select></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="email-new-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy || !f.contact_id || f.purpose.trim().length < 3} data-testid="email-new-submit">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Draft email</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
